import React, { useState } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQModal from '../../components/modal';
import UQButton from '../../components/button';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const ModalPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'warning' | 'info' | 'success' | 'error'>('info');
  const [isNeutralOpen, setIsNeutralOpen] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const attributes = [
    {
      attribute: 'show',
      type: 'boolean',
      default: 'false',
      description: 'Controls the visibility of the modal.',
    },
    {
      attribute: 'onClose',
      type: '() => void',
      default: 'undefined',
      description: 'Callback function triggered when the modal is closed.',
    },
    {
      attribute: 'style',
      type: "'warning' | 'info' | 'success' | 'error' | 'neutral'",
      default: "'info'",
      description: 'Defines the style of the modal. Use "neutral" for a plain modal without color or icon.',
    },
    {
      attribute: 'header',
      type: 'string',
      default: 'undefined',
      description: 'The title of the modal.',
    },
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display in the modal header.',
    },
    {
      attribute: 'buttoncancel',
      type: 'string',
      default: 'undefined',
      description: 'The text for the Cancel button. If undefined, the button will not appear.',
    },
    {
      attribute: 'buttonOk',
      type: 'string',
      default: 'undefined',
      description: 'The text for the OK button. If undefined, the button will not appear.',
    },
    {
      attribute: 'iconOk',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display in the OK button.',
    },
    {
      attribute: 'onOk',
      type: '() => void',
      default: 'undefined',
      description: 'Callback function triggered when the OK button is clicked.',
    },
    {
      attribute: 'children',
      type: 'React.ReactNode',
      default: 'undefined',
      description: 'The content to display inside the modal.',
    },
    {
      attribute: 'fullscreen',
      type: 'boolean',
      default: 'false',
      description: 'If true, the modal covers the screen with a margin.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const modalCodeSnippet = `
import React, { useState } from 'react';
import { UQModal, UQButton } from '@uniqconsulting/ui';

const ModalExample = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'warning' | 'info' | 'success' | 'error'>('info');

  return (
    <>
      <UQButton type="primary" onClick={() => { setModalType('info'); setIsModalOpen(true); }}>
        Open Info Modal
      </UQButton>
      <UQModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        style={modalType}
        header="Modal Title"
        icon="info"
        buttoncancel="Cancel"
        buttonOk="Save"
        iconOk="save"
        onOk={() => {
          console.log('Ok button clicked');
          setIsModalOpen(false);
        }}
      >
        <p>This is the content of the modal.</p>
      </UQModal>
    </>
  );
};
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Modal']} />
      <h1>UQModal Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQModal</code> component is a versatile modal dialog that supports various styles, a neutral mode, and a fullscreen mode. The close <b>X</b> is always shown in the top right.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      <UQSpacer size="s" />
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <UQButton type="primary" onClick={() => { setModalType('info'); setIsModalOpen(true); }}>
          Open Info Modal
        </UQButton>
        <UQButton type="primary" color="yellow" onClick={() => { setModalType('warning'); setIsModalOpen(true); }}>
          Open Warning Modal
        </UQButton>
        <UQButton type="primary" color="green" onClick={() => { setModalType('success'); setIsModalOpen(true); }}>
          Open Success Modal
        </UQButton>
        <UQButton type="primary" color="salmon" onClick={() => { setModalType('error'); setIsModalOpen(true); }}>
          Open Error Modal
        </UQButton>
        <UQButton type="secondary" onClick={() => setIsNeutralOpen(true)}>
          Open Neutral Modal
        </UQButton>
        <UQButton type="secondary" onClick={() => setIsFullscreenOpen(true)}>
          Open Fullscreen Modal
        </UQButton>
      </div>

      {/* Standard Modal */}
      <UQModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        style={modalType}
        header={
          modalType === 'info'
            ? 'Information'
            : modalType === 'warning'
              ? 'Warning'
              : modalType === 'success'
                ? 'Success'
                : 'Error'
        }
        icon={
          modalType === 'info'
            ? 'info'
            : modalType === 'warning'
              ? 'alert-triangle'
              : modalType === 'success'
                ? 'check-circle'
                : 'x-circle'
        }
        buttoncancel="Cancel"
        buttonOk="Save"
        iconOk="save"
        onOk={() => {
          alert("Save button clicked");
          setIsModalOpen(false);
        }}
      >
        <p>
          {modalType === 'info'
            ? 'This is an informational modal.'
            : modalType === 'warning'
              ? 'This is a warning modal. Please proceed with caution.'
              : modalType === 'success'
                ? 'This is a success modal. Your operation was successful!'
                : 'This is an error modal. Something went wrong.'}
        </p>
      </UQModal>

      {/* Neutral Modal */}
      <UQModal
        show={isNeutralOpen}
        onClose={() => setIsNeutralOpen(false)}
        style="neutral"
        header="Neutral Modal"
        buttoncancel="Close"
        buttonOk="Confirm"
        onOk={() => { setIsNeutralOpen(false); alert("You have confirmed!"); }}
      >
        <p>
          This is a neutral modal. It has no color or icon, just a header and content.
        </p>
      </UQModal>

      {/* Fullscreen Modal */}
      <UQModal
        show={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        style="neutral"
        header="Fullscreen Modal"
        fullscreen
        buttoncancel="Close"
        buttonOk="Confirm"
        onOk={() => { setIsFullscreenOpen(false); alert("You have confirmed!"); }}
      >
        <p>
          This is a fullscreen modal. It covers most of the screen with a margin around it.
        </p>
      </UQModal>

      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <p>
        Below is an example of how to use the <code>UQModal</code> component.
      </p>
      <UQSpacer size="s" />
      <UQCode code={modalCodeSnippet} enableCopy />
    </>
  );
};

export default ModalPage;
